// client/src/components/VintedShop.jsx
// Vinted "compose and handoff" composer.
//
// Vinted has no public seller API, so we cannot publish on the user's behalf.
// This page lets the user draft a listing (title, description, category,
// brand, size, condition, price, package size, photos), then on "Open in
// Vinted" we copy the formatted text to their clipboard and open Vinted's
// new-listing page in a new tab so they can paste, attach photos, and tap
// publish themselves. Drafts persist in localStorage so they don't lose work.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaHome, FaExternalLinkAlt, FaTag, FaTrash, FaImage } from 'react-icons/fa';
import AppFooter from './AppFooter';

const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin || 'http://localhost:5000';
const VINTED_TEAL = '#09B1BA';

const EMPTY_DRAFT = {
  title: '',
  description: '',
  category: '',
  brand: '',
  size: '',
  color: '',
  condition: '',
  price: '',
  packageSize: '',
  locale: 'com',
};

function VintedShop() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const tenantId = Number(tenant) || Number(localStorage.getItem('tenantId')) || 1;
  const draftStorageKey = `vintedDraft:${tenantId}`;

  const [enums, setEnums] = useState({ conditions: [], packageSizes: [], categories: [], locales: [] });
  const [enumsLoading, setEnumsLoading] = useState(true);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState('');
  const [handoffPending, setHandoffPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${baseUrl}/api/integrations/vinted/enums`, { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setEnums(data);
      })
      .catch((err) => {
        if (!cancelled) setStatus(`Could not load Vinted options: ${err.message}`);
      })
      .finally(() => { if (!cancelled) setEnumsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(draftStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        setDraft({ ...EMPTY_DRAFT, ...parsed });
      }
    } catch {
      // ignore parse errors and start fresh
    }
  }, [draftStorageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(draftStorageKey, JSON.stringify(draft));
    } catch {
      // storage quota / private mode — non-fatal
    }
  }, [draft, draftStorageKey]);

  const updateField = (field) => (e) => {
    const value = e?.target?.type === 'checkbox' ? e.target.checked : e?.target?.value;
    setDraft((d) => ({ ...d, [field]: value }));
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const next = files.map((file) => ({
      id: `${file.name}-${file.size}-${Date.now()}`,
      file,
      url: URL.createObjectURL(file),
      name: file.name,
    }));
    setPhotos((p) => [...p, ...next]);
    e.target.value = '';
  };

  const removePhoto = (id) => {
    setPhotos((p) => {
      const target = p.find((x) => x.id === id);
      if (target?.url) URL.revokeObjectURL(target.url);
      return p.filter((x) => x.id !== id);
    });
  };

  useEffect(() => () => {
    // revoke object URLs on unmount
    photos.forEach((p) => p.url && URL.revokeObjectURL(p.url));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadPhotos = () => {
    photos.forEach((p, idx) => {
      const a = document.createElement('a');
      a.href = p.url;
      const ext = p.name.split('.').pop() || 'jpg';
      a.download = `vinted-${idx + 1}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    });
  };

  const clearDraft = () => {
    if (!window.confirm('Clear this draft? It will be removed permanently.')) return;
    photos.forEach((p) => p.url && URL.revokeObjectURL(p.url));
    setPhotos([]);
    setDraft(EMPTY_DRAFT);
    setStatus('Draft cleared.');
  };

  const handleHandoff = async () => {
    if (!draft.title.trim()) {
      setStatus('Add a title before opening Vinted.');
      return;
    }
    setHandoffPending(true);
    setStatus('');
    try {
      const resp = await fetch(`${baseUrl}/api/integrations/vinted/handoff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(draft),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'Handoff failed');

      try {
        await navigator.clipboard.writeText(data.clipboardText || '');
      } catch {
        // clipboard API may be blocked; user will still get the Vinted page
      }

      const opened = window.open(data.targetUrl, '_blank', 'noopener,noreferrer');
      if (!opened) {
        setStatus(`Popup blocked — open Vinted manually: ${data.targetUrl}`);
      } else {
        setStatus('Listing copied to clipboard. Paste it into Vinted, attach your photos, and publish.');
      }
    } catch (err) {
      setStatus(`Error: ${err.message}`);
    } finally {
      setHandoffPending(false);
    }
  };

  const handleDashboard = () => navigate(`/${tenantId}/dashboard`);

  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' };
  const inputStyle = {
    width: '100%', padding: '8px 10px', fontSize: '14px',
    border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box',
  };
  const fieldGroup = { marginBottom: '12px' };
  const rowStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' };

  const statusStyle = useMemo(() => {
    const isError = status.toLowerCase().includes('error') || status.toLowerCase().includes('blocked') || status.toLowerCase().includes('could not');
    return {
      padding: '8px 12px', marginBottom: '12px', borderRadius: '6px',
      backgroundColor: isError ? '#fef2f2' : '#ecfeff',
      color: isError ? '#dc2626' : '#0e7490',
      fontSize: '14px',
    };
  }, [status]);

  return (
    <div style={{ padding: '12px', maxWidth: '720px', margin: '0 auto', paddingBottom: '80px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>
          <FaTag style={{ marginRight: '8px', color: VINTED_TEAL }} />
          Vinted Composer
        </h2>
        <button
          onClick={clearDraft}
          title="Clear draft"
          style={{
            background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px',
            padding: '6px 10px', cursor: 'pointer', color: '#6b7280', fontSize: '13px',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}
        >
          <FaTrash /> Clear
        </button>
      </div>

      <div style={{
        background: '#ecfeff', border: '1px solid #a5f3fc', color: '#0e7490',
        padding: '10px 12px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px',
      }}>
        Vinted doesn't offer a public seller API, so OnScreen can't publish for you.
        Draft your listing here — when you tap <b>Open in Vinted</b>, we'll copy the text
        to your clipboard and open Vinted's new-listing page. Paste, attach your photos, and publish there.
      </div>

      {status && <div style={statusStyle}>{status}</div>}

      {enumsLoading ? (
        <p style={{ color: '#6b7280' }}>Loading…</p>
      ) : (
        <>
          <div style={fieldGroup}>
            <label style={labelStyle}>Title</label>
            <input
              type="text"
              value={draft.title}
              onChange={updateField('title')}
              placeholder="e.g. Vintage Levi's 501 jeans, dark wash, W30 L32"
              maxLength={50}
              style={inputStyle}
            />
            <small style={{ color: '#9ca3af' }}>{draft.title.length}/50</small>
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Description</label>
            <textarea
              value={draft.description}
              onChange={updateField('description')}
              rows={5}
              placeholder="Condition notes, measurements, materials, why you're selling…"
              maxLength={1500}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
            <small style={{ color: '#9ca3af' }}>{draft.description.length}/1500</small>
          </div>

          <div style={rowStyle}>
            <div style={fieldGroup}>
              <label style={labelStyle}>Category</label>
              <select value={draft.category} onChange={updateField('category')} style={inputStyle}>
                <option value="">— pick one —</option>
                {enums.categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div style={fieldGroup}>
              <label style={labelStyle}>Condition</label>
              <select value={draft.condition} onChange={updateField('condition')} style={inputStyle}>
                <option value="">— pick one —</option>
                {enums.conditions.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={rowStyle}>
            <div style={fieldGroup}>
              <label style={labelStyle}>Brand</label>
              <input type="text" value={draft.brand} onChange={updateField('brand')} placeholder="e.g. Levi's" style={inputStyle} />
            </div>
            <div style={fieldGroup}>
              <label style={labelStyle}>Size</label>
              <input type="text" value={draft.size} onChange={updateField('size')} placeholder="e.g. M, 38, W30 L32" style={inputStyle} />
            </div>
          </div>

          <div style={rowStyle}>
            <div style={fieldGroup}>
              <label style={labelStyle}>Color</label>
              <input type="text" value={draft.color} onChange={updateField('color')} placeholder="e.g. Navy" style={inputStyle} />
            </div>
            <div style={fieldGroup}>
              <label style={labelStyle}>Price</label>
              <input type="text" inputMode="decimal" value={draft.price} onChange={updateField('price')} placeholder="e.g. 24.00" style={inputStyle} />
            </div>
          </div>

          <div style={rowStyle}>
            <div style={fieldGroup}>
              <label style={labelStyle}>Package size</label>
              <select value={draft.packageSize} onChange={updateField('packageSize')} style={inputStyle}>
                <option value="">— pick one —</option>
                {enums.packageSizes.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>
            <div style={fieldGroup}>
              <label style={labelStyle}>Vinted marketplace</label>
              <select value={draft.locale} onChange={updateField('locale')} style={inputStyle}>
                {enums.locales.map((l) => (
                  <option key={l.id} value={l.id}>{l.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Photos (saved locally — download to upload in Vinted)</label>
            <input type="file" accept="image/*" multiple onChange={handlePhotoSelect} />
            {photos.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                {photos.map((p) => (
                  <div key={p.id} style={{ position: 'relative', width: '80px', height: '80px' }}>
                    <img
                      src={p.url}
                      alt={p.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                    />
                    <button
                      onClick={() => removePhoto(p.id)}
                      title="Remove"
                      style={{
                        position: 'absolute', top: '-6px', right: '-6px',
                        width: '20px', height: '20px', borderRadius: '50%',
                        border: 'none', background: '#ef4444', color: '#fff',
                        cursor: 'pointer', fontSize: '11px', lineHeight: '1',
                      }}
                    >×</button>
                  </div>
                ))}
              </div>
            )}
            {photos.length > 0 && (
              <button
                onClick={downloadPhotos}
                style={{
                  marginTop: '10px', padding: '6px 12px', fontSize: '13px',
                  background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px',
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px',
                }}
              >
                <FaImage /> Download {photos.length} photo{photos.length === 1 ? '' : 's'} for Vinted
              </button>
            )}
          </div>

          <button
            onClick={handleHandoff}
            disabled={handoffPending}
            style={{
              width: '100%', marginTop: '8px', padding: '12px 16px',
              backgroundColor: handoffPending ? '#9ca3af' : VINTED_TEAL, color: '#fff',
              border: 'none', borderRadius: '6px', fontSize: '15px', fontWeight: 600,
              cursor: handoffPending ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            }}
          >
            <FaExternalLinkAlt /> {handoffPending ? 'Preparing…' : 'Open in Vinted'}
          </button>
        </>
      )}

      <AppFooter buttons={[
        { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(-1) },
        { icon: <FaHome />, label: 'Home', onClick: handleDashboard },
      ]} />
    </div>
  );
}

export default VintedShop;
